import { memo, useState, useRef, useEffect } from 'react';
import { getProductTypeOptions } from '../../../utils/insuranceTypeUtils';

/**
 * 保険商品種類（カテゴリ）のポップアップ選択コンポーネント
 *
 * 生損保区分に応じたカテゴリ選択肢を表示し、複数選択をサポートする。
 *
 * @param {object} props
 * @param {number} props.insuranceType - 生損保区分（INSURANCE_TYPE.LIFE | INSURANCE_TYPE.SONPO）
 * @param {string[]} props.selectedValues - 選択中のカテゴリ値リスト
 * @param {Function} props.onChange - 選択変更コールバック。引数: (newValues: string[])
 */
const ProductTypeSelect = ({ insuranceType, selectedValues, onChange }) => {
  const [showPopup, setShowPopup] = useState(false);
  const fieldRef = useRef(null);

  const options = getProductTypeOptions(insuranceType);
  // options が空のとき「全選択」と誤認しないよう length > 0 でガードする
  const isAllSelected = options.length > 0 && options.every(opt => selectedValues.includes(opt));

  const togglePopup = () => setShowPopup(prev => !prev);

  const handleSelect = (value) => {
    // スプレッドでイミュータブルに配列を更新する
    if (selectedValues.includes(value)) {
      onChange(selectedValues.filter(v => v !== value));
    } else {
      onChange([...selectedValues, value]);
    }
  };

  const handleToggleAll = () => {
    // 全選択済みなら全解除、未全選択なら全オプションを選択
    onChange(isAllSelected ? [] : [...options]);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (fieldRef.current && !fieldRef.current.contains(e.target)) {
        setShowPopup(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const renderDisplayText = () => {
    if (selectedValues.length === 0) {
      return '指定なし';
    }
    return (
      <div className="dc-selected-list">
        {/* UI スペース制約により表示は最大 5 件。超過分は「他N件」で集約 */}
        {selectedValues.slice(0, 5).map((v, i) => (
          <div key={i} className="dc-selected-item">{v}</div>
        ))}
        {selectedValues.length > 5 && (
          <div className="dc-selected-item">他{selectedValues.length - 5}件</div>
        )}
      </div>
    );
  };

  return (
    <div className="dc-popup-field" ref={fieldRef}>
      <div className="dc-trigger" onClick={togglePopup}>
        <span className="dc-trigger-label">
          保険商品種類
          <svg width="14" height="15" viewBox="0 0 14 15" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M1 3V14H11" stroke="#0073BC" strokeWidth="2"/>
            <rect x="5" y="1" width="8" height="9" fill="white" stroke="#0073BC" strokeWidth="2"/>
          </svg>
        </span>
        <span className="dc-trigger-value">{renderDisplayText()}</span>
      </div>
      {showPopup && (
        <div className="dc-popup dc-popup--product-type">
          <div className="dc-popup-header">
            <span>保険商品種類</span>
            <div className="dc-popup-header-actions">
              <button type="button" className="dc-popup-close-btn" onClick={togglePopup}>
                選択完了
              </button>
            </div>
          </div>
          <div className="dc-popup-body dc-checkbox-grid">
            {options.length === 0 ? (
              <p className="dc-popup-empty">選択可能な保険商品種類がありません。</p>
            ) : (
              <>
                <div className="dc-select-all-row">
                  <label className="checkbox-native-box">
                    <input
                      className="checkbox-native"
                      type="checkbox"
                      id={`dc-category-all-${insuranceType}`}
                      checked={isAllSelected}
                      onChange={handleToggleAll}
                    />
                  </label>
                  <label htmlFor={`dc-category-all-${insuranceType}`}>全て選択</label>
                </div>
                {options.map(opt => {
                  // id 属性値にスペースを含められないためハイフンに置換する
                  const cbId = `dc-category-${insuranceType}-${opt.replace(/\s+/g, '-')}`;
                  return (
                    <div key={opt} className="dc-checkbox-item">
                      <label className="checkbox-native-box">
                        <input
                          className="checkbox-native"
                          type="checkbox"
                          id={cbId}
                          checked={selectedValues.includes(opt)}
                          onChange={() => handleSelect(opt)}
                        />
                      </label>
                      <label htmlFor={cbId}>{opt}</label>
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default memo(ProductTypeSelect);
